import type { Catalog } from '../types';

// Checkout steps: PaymentStep, PaymentForm, TrialPaymentStep, TrialPaymentForm,
// PromoCodeInput, TrialIneligibleStep, SecureRedirectStep, ErrorStep, SuccessStep,
// plus the promo chip descriptor and the flows' fallback error copy.
// Server-delivered strings (Stripe/API error messages, product names) are NOT
// here — they render verbatim.
export const checkout: Catalog = {
  'upgrade.title': 'Upgrade your plan',
  'upgrade.current': 'Current plan',
  'upgrade.approxLocal': 'About {amount}',
  'upgrade.loading': 'Loading upgrade...',
  'upgrade.success': 'Your plan has been upgraded',
  'upgrade.charged': 'Charged {amount}',
  'upgrade.continue': 'Continue',
  'upgrade.updatePayment': 'Update payment method',
  'upgrade.reason.cancel_pending': 'Your subscription is scheduled to cancel. Remove the cancellation before upgrading.',
  'upgrade.reason.renewal_pending': 'Your renewal payment is still pending. Please try again after it completes.',
  'upgrade.reason.upgrade_in_progress': 'An upgrade is already in progress. Please check its status.',
  'upgrade.reason.card_declined': 'Your card was declined. Update your payment method to continue.',
  'upgrade.reason.payment_method_required': 'Add a payment method to upgrade.',
  'upgrade.reason.quote_changed': 'The upgrade amount has changed. Review the new amount and confirm again.',
  'upgrade.reason.quote_expired': 'Your quote expired. Review the refreshed amount and confirm again.',
  'upgrade.reason.pending': 'Your upgrade is still processing. Please check its status.',
  'upgrade.reason.generic': 'This upgrade could not be completed. Please try again or manage your subscription.',
  'upgrade.chooseHint': 'Choose a plan to review your upgrade.',
  'upgrade.options': 'Available upgrades',
  'upgrade.reviewTitle': 'Confirm upgrade',
  'upgrade.reviewHint': 'Your new plan will start immediately.',
  'upgrade.unusedCredit': 'Credit for unused time',
  'upgrade.otherAdjustments': 'Account balance and other items',
  'upgrade.thenFrom': 'Then {price} starting {date}.',
  'upgrade.trialCurrent': 'Free trial · ends {date}',
  'upgrade.trialEndsNote': 'Upgrading your plan will end your free trial.',
  'upgrade.paymentNote': 'Confirming charges your saved payment method.',
  'upgrade.confirmAmount': 'Confirm upgrade · {amount}',
  'upgrade.successHint': 'Your new plan is ready to use.',
  'upgrade.reason.session_expired': 'Your session has expired. Sign in again to continue.',
  'upgrade.reason.forbidden': 'Changes to this subscription aren’t available in this session.',
  // Inline payment method row and card form in the upgrade review.
  'upgrade.payment.label': 'Paying with',
  'upgrade.payment.endingIn': 'Ending in {last4}',
  'upgrade.payment.saved': 'Saved payment method',
  'upgrade.payment.change': 'Change',
  'upgrade.payment.useSaved': 'Use saved payment method',
  'upgrade.payment.newCard': 'New card',
  'upgrade.payment.newCardAdded': 'New card added',
  'upgrade.payment.newCardNote': 'Confirming verifies your new card and charges it.',
  'upgrade.payment.renewalNote': 'Your new payment method will also be used for this subscription’s renewals, even if the upgrade does not complete.',
  'upgrade.payment.loadFailed': 'We couldn’t load the card form. Please try again.',
  'upgrade.payment.setupFailed': 'Your card couldn’t be verified. Please try again or use a different card.',
  'upgrade.payment.declined': 'Your payment was declined. Choose a different payment method to continue.',
  'upgrade.payment.required': 'Choose a payment method to continue with your upgrade.',
  'upgrade.payment.applePayAuthorize': 'Apple Pay · Authorize again',
  'upgrade.reason.wallet_authorization_required': 'Authorize Apple Pay again or use a different payment method to upgrade.',
  'upgrade.payment.renewalUncertain': 'If your new payment method was saved, it remains selected for renewals even if this upgrade did not complete.',
  'upgrade.payment.walletFailed': 'Your wallet could not be authorized. Try again or use a card.',
  'upgrade.deferredBalance': 'No payment collected today. {amount} was carried forward to a future invoice.',
  'upgrade.creditCarryover': 'Your remaining {amount} credit will go toward future payments.',
  'upgrade.paymentNoteNoCharge': 'Nothing is charged today. Renewals use this payment method.',
  // Shared chrome across steps.
  'checkout.tryAgain': 'Try again',
  'checkout.loadFailed': 'We could not load checkout. Please try again in a moment.',
  'checkout.unknownError': 'Unknown error',
  // Flow-level fallbacks (CheckoutFlow): rendered when an
  // API failure carries no message of its own.
  'checkout.errorGeneric': 'Something went wrong',
  'checkout.magicLinkFailed': 'Unable to send login link',
  'checkout.sessionInvalid': 'The checkout session is no longer valid.',
  'checkout.convertProcessing': 'Your subscription is still being set up. Please check again in a moment.',

  'paymentStep.eyebrow': 'Payment',
  'paymentStep.title': 'Payment details',
  'paymentStep.subtitle': 'Enter payment details below.',

  'paymentForm.payNow': 'Pay now',
  'paymentForm.processing': 'Processing...',
  'paymentForm.checkStatus': 'Check status',
  'paymentForm.orPayWithCard': 'Or pay with card',
  'paymentForm.loadFailed': 'Failed to load payment form',
  'paymentForm.startFailed': 'Payment could not be started. Please try again.',
  'paymentForm.failed': 'Payment failed. Please try again.',
  'paymentForm.sessionExpired': 'This checkout session has expired. Please start again.',
  'paymentForm.incomplete': 'Payment could not be completed. Please try again.',
  'paymentForm.stillProcessing': 'Your payment is still processing. Please check again in a moment.',
  'paymentForm.confirmedButIncomplete': 'Your payment is confirmed, but we could not finish checkout. Please check the status again. {detail}',
  'paymentForm.failedWithDetail': 'Payment could not be completed. Please try again. {detail}',

  'trialPayment.eyebrow': 'Free trial',
  'trialPayment.title': 'Start your free trial',
  'trialPayment.startFreeTrial': 'Start free trial',
  'trialPayment.starting': 'Starting trial...',
  // Fragment: the masked email renders after it wrapped in <strong>.
  'trialPayment.startingAs': 'Starting trial as',
  'trialPayment.subtitle': "Enter a card to start your trial. You won't be charged until {date}, and you can cancel anytime before then.",
  'trialPayment.subtitleNoDate': "Enter a card to start your trial. You won't be charged until it ends, and you can cancel anytime.",
  'trialPayment.orEnterCard': 'Or enter a card',
  'trialPayment.stillProcessing': 'Your trial is still being set up. Please check again in a moment.',
  'trialPayment.startFailedWithDetail': 'Your trial could not be started. Please try again. {detail}',
  'trialPayment.cardSetupFailed': 'Card setup failed. Please try again.',
  'trialPayment.cardNotSetUp': 'Card could not be set up. Please try again.',

  'promo.toggle': 'Have a promo code?',
  'promo.label': 'Promo code',
  'promo.placeholder': 'Enter code',
  'promo.apply': 'Apply',
  'promo.remove': 'Remove',
  'promo.invalid': "Promo code {code} isn't valid.",
  'promo.applyFailed': 'Promo code {code} could not be applied.',
  'promo.applyFailedRetry': 'This promo code could not be applied. Please try again.',
  'promo.removeFailed': 'This promo code could not be removed.',
  'promo.removeFailedRetry': 'This promo code could not be removed. Please try again.',
  // Field messages keyed by the backend's machine reason (trial path).
  'promo.reason.expired': 'Promo code {code} has expired.',
  'promo.reason.inactive': 'Promo code {code} is no longer active.',
  'promo.reason.productNotEligible': "Promo code {code} isn't valid for this plan.",
  'promo.reason.minimumNotMet': "This plan doesn't meet the minimum spend for promo code {code}.",
  'promo.reason.redemptionLimit': 'Promo code {code} is no longer available.',
  'promo.reason.currencyNotEligible': "Promo code {code} isn't valid in your currency.",
  // Applied-chip summaries (promoDescriptor), built from a pre-formatted
  // {magnitude} ("25%" or "$5.00").
  'promo.chip.off': '{magnitude} off',
  'promo.chip.offFirstPayment': '{magnitude} off first payment',
  'promo.chip.offForever': '{magnitude} off forever',
  'promo.chip.offLimited': '{magnitude} off for a limited time',
  'promo.chip.offMonths': {
    one: '{magnitude} off for {count} month',
    other: '{magnitude} off for {count} months',
  },

  'trialIneligible.eyebrow': 'Trial unavailable',
  'trialIneligible.title': "It looks like you've already redeemed a free trial",
  'trialIneligible.subtitle': 'No problem — you can subscribe now to get access right away.',
  'trialIneligible.subtitleWithPrice': 'No problem — you can subscribe now for {price}, billed today, and get access right away.',
  'trialIneligible.ack': "I understand I won't get a free trial and will be charged today.",
  'trialIneligible.ackWithPrice': "I understand I won't get a free trial and will be charged {price} today.",
  // Discounted-convert variants (the validate preview's first-charge totals):
  // once bills the discounted amount today and the catalog price after;
  // repeating bills it for the exact month count first. Forever reuses
  // ackWithPrice with the discounted amount (it is the recurring price).
  'trialIneligible.ackDiscountOnce': "I understand I won't get a free trial and will be charged {today} today, then {price}.",
  'trialIneligible.ackDiscountRepeating': {
    one: "I understand I won't get a free trial and will be charged {today} for the first month, then {price}.",
    other: "I understand I won't get a free trial and will be charged {today} for the first {count} months, then {price}.",
  },
  'trialIneligible.subscribeNow': 'Subscribe now',
  'trialIneligible.subscribeFor': 'Subscribe for {price}',
  // A once-duration discount: today's charge is the one-time amount, so the
  // button states it instead of the recurring price.
  'trialIneligible.subscribeToday': 'Subscribe for {today} today',
  'trialIneligible.starting': 'Starting subscription...',
  'trialIneligible.reason.authentication_failed': 'Your card couldn’t be verified. Please try again or add a new payment method.',
  'trialIneligible.reason.card_declined': 'Your card was declined. Please try again or add a new payment method.',
  'trialIneligible.addPaymentMethod': 'Add new payment method',

  'secureRedirect.eyebrow': 'Secure checkout',
  'secureRedirect.title': 'One quick step to keep your payment safe',
  'secureRedirect.subtitle': "This page loaded over an unsecured connection. To protect your payment details, we'll reload this page over a secure connection — your selection is saved and you'll continue right where you left off.",
  'secureRedirect.unavailableTitle': 'Secure checkout is unavailable right now',
  'secureRedirect.unavailableSubtitle': "We couldn't establish a secure connection to this site, so checkout can't continue. Please try again in a few minutes, or contact the site owner if the problem continues.",
  // Shown under "Try again" after a re-probe also fails, so the button reads
  // as answered rather than dead.
  'secureRedirect.stillUnavailable': "We still can't establish a secure connection.",
  'secureRedirect.continue': 'Continue to secure checkout',

  'errorStep.ariaLabel': 'Checkout error',
  'errorStep.eyebrow': 'Something went wrong',
  'errorStep.title': "We couldn't start checkout",

  'inline.checkingAccess': 'Checking access…',
  'inline.configFailed.transient': 'Checkout could not load right now. Please try again.',
  'inline.configFailed.permanent': 'This checkout is not available. Contact the site owner if you expected to buy here.',
  'inline.domainUnavailable': 'Checkout is not available on this site right now.',
  'inline.accessFailed': 'We could not check your access. Please try again.',
  'success.title.owned': 'You already have access',

  'success.ariaLabel.member': 'Already a member',
  'success.ariaLabel.trial': 'Free trial started',
  'success.ariaLabel.paid': 'Payment successful',
  'success.eyebrow.member': 'Welcome back',
  'success.eyebrow.trial': 'Trial started',
  'success.eyebrow.paid': 'Confirmed',
  'success.title.member': "You're all set",
  'success.title.trial': 'Your free trial has started',
  'success.title.paid': 'Payment complete',
  'success.subtitle.member': 'You already have access to this content.',
  'success.subtitle.trial': "Your free trial is active and you now have full access. We'll let you know before it ends.",
  'success.subtitle.paid': 'Your purchase is confirmed. You now have access to this content.',
  // A donation grants no access, so it gets its own confirmation line.
  'success.subtitle.donation': 'Thank you for your support. Your donation is confirmed.',
  // Fragment: the masked email renders after it wrapped in <strong>.
  'success.email': 'Email:',
  'success.portalHeading.manage': 'Manage your account',
  'success.portalHeading.email': 'Check your email',
  'success.portalBody.manageRecurring': 'Update payment methods, view receipts, and cancel anytime from your account.',
  'success.portalBody.manageOneTime': 'View your receipts and manage payment methods anytime from your account.',
  'success.portalBody.receiptRecurring': 'We have sent your confirmation and receipt. Manage payment methods, view receipts, and cancel anytime from your account.',
  'success.portalBody.receiptOneTime': 'We have sent your receipt. View your receipts and manage payment methods anytime from your account.',
  'success.portalLink.manage': 'Manage your subscription',
  'success.portalLink.account': 'Go to your account',
};
